import axios from 'axios'
import { trackSignUp } from '../util/Analytics'
import { BigNumber } from 'ethers'
import { makeAutoObservable } from 'mobx'
import {
  API_BASE_URL,
  RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY,
  RADOM_TOKEN_LOCAL_STORAGE_KEY
} from '../util/Constants'
import { errorToast, identifyVisitor, successToast } from '../util/Util'
import UserState from './User'

interface IAuthRequestResponse {
  nonce: string
}

interface IAuthTokenResponse {
  jwtToken: string
  requires2fa: boolean
  expiresAt: string
  isNewUser?: boolean
}

export interface LatestAck {
  chainId?: number
  lastBlockAck?: number
  managedLastAck?: string
}

export type VerificationStatus =
  | 'Unverified'
  | 'PendingVerificationSubmission'
  | 'PendingApproval'
  | 'Rejected'
  | 'Verified'

export interface BusinessBasics {
  name: string
  description: string
  businessType: BusinessTypes
  businessIndustry: string[]
  website: string
  email: string
  businessActivityPeriod: string
  businessRegistrationNumber: string
}

export interface Address {
  streetLine1: string
  streetLine2?: string
  city: string
  subdivision?: string
  postalCode?: string
  country: string
}

export interface BusinessAddress {
  registeredAddress: Address
  physicalAddress: Address
  proofOfAddress: string
}

export interface IAccountUsage {
  accountServices: AccountServicesType
  productsUsage: string
  monthlyVolume: string
  paymentCounterparties: string
  displayName: string
  logo?: string
  discoverRadom: string
}

export enum WebhookMessagesStatus {
  Pending = 'Pending',
  Success = 'Success',
  Failed = 'Failed',
  WebhookDoesNotExist = 'Webhook Does Not Exist',
}

export interface LegalAndTaxDetails {
  expectedCryptoActivity: string
  expectedFiatActivity: string
  expectedOriginsOfFunds: string
  sourceOfFunds: string
  taxIdentificationNumberType: string
  taxIdentificationNumber: string
  proofOfWealth: string
}

export interface FinancialInformation {
  sourceOfFunds: string
  statementOfFunds: string
}

export interface RegulatoryCompliance {
  transmitsCustomerFunds: boolean
  complianceScreeningExplanation: string
}

export interface Ubo {
  firstName: string
  lastName: string
  birthDate: string
  email: string
  phone: string
  residentialAddress: Address | undefined
  title?: string | undefined
  hasOwnership: boolean
  hasControl: boolean
  isSigner: boolean
  relationshipEstablishedAt: string | undefined
  taxIdentificationNumberType: string | undefined
  taxIdentificationNumber: string | undefined
  govIdImageCountry: string
  govIdImageType: string
  govIdImage: string
  govIdImageBack?: string
  govIdExpirationDate?: string
  proofOfAddress: string | undefined
}

export interface AdditionalDocument {
  file: string
  fileName?: string
  url?: string
}

export const MAX_ADDITIONAL_DOCUMENTS = 20

export interface Documents {
  formationDocument?: string
  ownershipDocument?: string
  activityDocument?: string
  additionalDocuments?: AdditionalDocument[]
  formationOrOwnershipDocument?: string
}

export interface IVerification {
  status: VerificationStatus
  adminUpdatedAt?: string
  name?: string
  accountServices?: AccountServicesType
  productsUsage?: string
  monthlyVolume?: string
  paymentCounterparties?: string
  displayName?: string
  logo?: string
  logoUrl?: string
  discoverRadom?: string
  description?: string
  businessType?: BusinessTypes
  businessActivityPeriod?: string
  businessRegistrationNumber?: string
  expectedCryptoActivity?: string
  expectedFiatActivity?: string
  expectedOriginsOfFunds?: string
  businessIndustry?: string[]
  website?: string
  email?: string
  registeredAddress?: Address
  physicalAddress?: Address
  proofOfAddress?: string
  proofOfAddressUrl?: string
  accountPurpose?: string
  sourceOfFunds?: string
  hasMaterialIntermediaryOwnership?: boolean
  taxIdentificationNumberType?: string
  taxIdentificationNumber?: string
  formationDocument?: string
  formationDocumentUrl?: string
  ownershipDocument?: string
  ownershipDocumentUrl?: string
  proofOfWealth?: string
  proofOfWealthUrl?: string
  activityDocument?: string
  activityDocumentUrl?: string
  additionalDocuments?: AdditionalDocument[]
  formationOrOwnershipDocument?: string
  formationOrOwnershipDocumentUrl?: string
  associatedPersons?: Ubo[]
  requirements?: VerificationRequirement[]
}

export interface IPaymentFee {
  feePercent: string
  networkFeePercent: string
  networkFeeMinUsd: string
  networkFeeMaxUsd: string
}

export interface ISwapFee {
  feePercent: string
  flatFeeUsd: string
  minFeeUsd?: string
}

export interface IOpenBankingFee {
  feePercent: string
  minFeeEur: string
}

export interface IFlatAndPercentFee {
  feePercent: string
  flatFeeCurrency: string
  flatFeeAmount: string
}

export interface IConversionFee {
  feePercent: string
  stableFeePercent?: string
  volatileFeePercent?: string
  crossChainFeePercent?: string
}

export interface IOrgFees {
  base?: IPaymentFee
  presale?: IPaymentFee
  donations?: IPaymentFee
  deposit?: IPaymentFee
  ach?: ISwapFee
  sepa?: ISwapFee
  sepaB2c?: ISwapFee
  spei?: ISwapFee
  pix?: ISwapFee
  wire?: ISwapFee
  swift?: ISwapFee
  openBankingFee?: IOpenBankingFee
  openBankingRefund?: IFlatAndPercentFee
  virtualAccountSepa?: IFlatAndPercentFee
  virtualAccountAch?: IFlatAndPercentFee
  virtualAccountSpei?: IFlatAndPercentFee
  virtualAccountPix?: IFlatAndPercentFee
  virtualAccountFedwire?: IFlatAndPercentFee
  virtualAccountConversion?: IFlatAndPercentFee
  conversion?: IConversionFee
}

export enum BusinessTypes {
  cooperative = 'Cooperative',
  corporation = 'Corporation',
  llc = 'LLC',
  other = 'Other',
  partnership = 'Partnership',
  sole_prop = 'Sole Proprietorship',
  trust = 'Trust',
}

export enum AccountServicesType {
  crypto_only = 'Crypto',
  crypto_and_fiat = 'CryptoFiat',
}

export type UpdateVerificationPayload =
  | IAccountUsage
  | BusinessBasics
  | BusinessAddress
  | LegalAndTaxDetails
  | FinancialInformation
  | RegulatoryCompliance
  | Ubo[]
  | Documents
  | null

export interface IOrganization {
  organizationId: string
  name?: string
  logoUrl?: string
  parentOrganizationId?: string
  parentOrganizationName?: string
  isFboOrg?: boolean
  childOrganizationCount?: number
  fboOrganizationCount?: number
  website?: string
  telegram?: string
  discord?: string
  twitter?: string
  imageBytes?: any
  defaultTokens?: AcceptedToken[]
  features?: {
    isPresaleEnabled: boolean
    isOfframpTronEnabled: boolean
    isOpenBankingEnabled: boolean
    isConversionEnabled: boolean
    isFboEnabled: boolean
    isUsdtEnabled: boolean
    isMoneroEnabled: boolean
    isSwapEnabled: boolean
    isMultiLegSwapEnabled?: boolean
    isPrivateTreasuryEnabled: boolean
    isStablecoinPayoutsEnabled?: boolean
    autoSwapOnPaymentEnabled?: boolean
    autoSwapOnPaymentTarget?: {
      network: string
      token?: string | null
    } | null
  }
  defaultCurrency: string
  verificationId?: string
  verificationStatus: VerificationStatus
  legacyEurOfframpEnabled?: boolean
  bridgeCustomer?: BridgeCustomer
}

export interface IDefaultToken {
  chainId: number
  tokenAddress: string
}

export type ICreateSellerProfile = Omit<IOrganization, 'logoUrl'> & {
  defaultTokens?: IDefaultToken[]
  imageBytes?: number[]
}

export interface IProductCreate {
  name: string
  description?: string
  addOns: Array<{ name: string, price: BigNumber }>
  chargingIntervalSeconds?: number
  currency: string
  price: number
  quantity?: number
  sendSubscriptionEmails?: boolean
  productType?: {
    Presale?: {
      Token?: {
        decimals?: any
        ticker?: any
      }
    }
  }
}

export type IProductUpdate = Partial<IProductCreate> & { id: string }
export type IProduct = Omit<IProductCreate, 'image'> & {
  createdAt: string
  updatedAt: string
  id: string
  imageUrl: string
  isArchived: boolean
}

export interface ProductWithQuantity {
  product: IProduct
  quantity: number
}

export interface LineItem {
  invoiceId: string
  name: string
  quantity: number
  price: number
  currency?: string
}

export interface AcceptedToken {
  tokenAddress: string
  chainId: number
}

export enum InputFieldDataType {
  String = 'String',
  Number = 'Number',
  Email = 'Email',
  Address = 'Address',
  EthereumAddress = 'EVM Address',
  SolanaAddress = 'Solana Address',
}

export interface InputField {
  dataType: InputFieldDataType
  inputLabel: string
  isRequired: boolean
}

export type ICreateInputField = Omit<InputField, 'dataType'> & {
  dataType: string
}

export interface IPaymentMethod {
  network: string
  token?: string
}

export interface IManagedGateway {
  methods: IPaymentMethod[]
}

export interface ISelfCustodialGateway {
  tokens: AcceptedToken[]
}

export interface CreateUpdatePaymentLinkReq {
  products: string[]
  gateway: {
    selfCustodial?: ISelfCustodialGateway
    managed?: IManagedGateway
  }
  customizations: Customization
  cancelUrl: string
  successUrl: string
  inputFields: InputField[]
  sendEmailReceipt?: boolean
  chargeCustomerNetworkFee?: boolean
  presaleData?: PresaleRequestData
}

export interface PresaleRequestData {
  totalRaiseAmount?: string
  minimumPurchaseAmount?: string
  startDate?: string
  endDate?: string
  pricingStrategies?: {
    timeBased?: any
    goalBased?: any
  }
}

export interface IPaymentLink {
  cancelUrl: string
  successUrl: string
  id: string
  products: IProduct[]
  gateway: {
    selfCustodial?: ISelfCustodialGateway
    managed?: IManagedGateway
  }
  url: string
  createdAt: Date
  updatedAt: Date
  customizations: Customization
  inputFields: InputField[]
  sendEmailReceipt?: boolean
  chargeCustomerNetworkFee?: boolean
  isPresale?: boolean
  isArchived?: boolean
  presaleData?: PresaleData
}

export interface bonusLevel {
  greaterThan: number
  lessThan: number
  bonus: number
}

export interface amountBasedPricingStrategy {
  name: string
  targetAmount: string
  priceIncreaseTo: string
}

export interface PresaleData {
  tokenPresaleData?: {
    totalRaiseAmount?: string
    minimumPurchaseAmount?: string
    startsAt?: string
    endsAt?: string
    bonus?: bonusLevel[]
    pricingStrategies?: {
      timeBased?: {
        startsAt: string
        roundDurationSeconds: number
        priceIncreasePercentage: number
      }
      amountBased?: amountBasedPricingStrategy[]
      goalBased?: any
    }
    customizations?: {
      title?: string
      bonusTitle?: string
      titleFontSize?: string
      titleFontColor?: string
      titleFontFamily?: string
      hideTotalRaiseGoal?: boolean
    }
  }
}

export interface IDonationLink {
  bannerImageUrl?: string
  imageUrl?: string
  id: string
  url: string
  name: string
  currency: string
  createdAt: Date
  updatedAt: Date
  primaryButtonColor: string
  inputFields: InputField[]
  methods: IPaymentMethod[]
  tosUrl?: string
  isArchived?: boolean
  customizations?: {
    enableKycCheck: boolean
    allowedCountries: string[]
    warningMessage: string
  }
}

export interface ICreateCheckoutSession {
  gateway: {
    selfCustodial?: ISelfCustodialGateway
    managed?: IManagedGateway
  }
  currency?: string
  total?: number
  cancelUrl?: string
  successUrl?: string
}

export interface ICheckoutOrderDetails {
  id: string
  checkoutSessionId: string
  organizationId: string
  buyerAddress: string
  tokenAddress: string
  chainId: number
  createdAt: Date
  paidAt: Date
  orderData: ICheckoutOrderData[]
  orderProductData: ICheckoutProductOrderData[]
}

interface ICheckoutOrderData {
  id: string
  orderId: string
  key: string
  value: string
}

interface ICheckoutProductOrderData {
  id: string
  orderId: string
  productId: string
  key: string
  value: string
}

export interface Customization {
  leftPanelColor: string
  leftPanelGradient?: string
  primaryButtonColor: string
  slantedEdge: boolean
  allowDiscountCodes: boolean
  allowFreeInputAmount: boolean
}

export interface ICreatePaymentLink {
  products: string[]
  customizations: Customization
  inputFields?: ICreateInputField[]
  sendEmailReceipt?: boolean
}

export interface ICreateDonationLink {
  primaryButtonColor: string
  inputFields?: ICreateInputField[]
  sendEmailReceipt?: boolean
}

export interface IDiscountCode {
  id: string
  code: string
  model: any
  products: string[]
  startsAt?: string
  expiresAt?: string
  createdAt: string
  updatedAt: string
}

export interface Customer {
  id: string
  totalSpend: string
  email: string
  name: string
  billingAddress?: string
  residenceCountry?: string
  dob?: string
  phone?: string
  telegram?: string
  discord?: string
  kycData?: {
    selfieUrl?: string
    idCardUrl?: string
    passportUrl?: string
  }
  payments: Array<{
    managed?: ManagedPaymentDetails
    openBanking?: openBankingDetails
  }>
  tags: string[]
  createdAt: string
  accountType: string
  companyName: string
  companyNumber?: string
}

export type ICreateCustomer = Omit<Customer, 'id'>

export type CheckoutFunnelRail = 'crypto' | 'openBanking'
export type CheckoutFunnelSourceType = 'checkoutSession' | 'paymentLink' | 'invoice' | 'donationLink' | 'presale'

export interface CheckoutFunnelSummaryRow {
  bucketDate: string
  rail: CheckoutFunnelRail
  sourceType: CheckoutFunnelSourceType
  startedSessions: number
  initiatedSessions: number
  completedSessions: number
}

export interface Invoice {
  id: string
  gateway: {
    selfCustodial?: ISelfCustodialGateway
    managed?: IManagedGateway
  }
  organizationId: string
  seller?: IOrganization
  customer: Customer
  products: ProductWithQuantity[]
  lineItems: LineItem[]
  issuedAt: Date
  paidAt?: Date
  voidedAt?: Date
  overdueAt?: Date
  inputData: Array<{ key: string, value: string }>
  status: 'paid' | 'pending' | 'voided' | 'overdue'
  memo?: string
  url: string
  payment?: {
    managed?: ManagedPaymentDetails
  }
}

export interface ListInvoice {
  id: string
  invoiceUrl: string
  status: 'paid' | 'pending' | 'voided' | 'overdue'
  email: string
  currency: string
  amount: number
  issuedAt: Date
  paidAt?: Date
  voidedAt?: Date
  overdueAt?: Date
}

export type ICreateInvoice =
  & Omit<
  Invoice,
    | 'id'
    | 'organizationId'
    | 'customer'
    | 'issuedAt'
    | 'paidAt'
    | 'voidedAt'
    | 'status'
  >
  & { 'customerIds': string[] }
export type MultiCreateInvoice =
  & Omit<ICreateInvoice, 'customerIds' | 'organizationId'>
  & { customers: Customer[], customer?: Customer }

export interface IToken {
  name: string
  permissions?: string[]
  createdAt: string
}

export interface ICreateApiTokenResponse {
  token: string
}

export interface IApiTokenListItem extends IToken {
  id: number
  publicId?: string
}

export interface OtpAuthResponse {
  otpAuthUrl: string
  otpBase32: string
}

export interface IWebhook {
  id: string
  url: string
  organizationId: string
  verificationKey?: string
  isPaused: boolean
  isDeleted: boolean
  createdTimestamp: string
  pausedTimestamp: string
}

export interface IWebhookMessage {
  id: string
  webhookId: string
  messagePayload: any
  messageStatus: string
  createdAt: Date
  nextDeliveryAttemptTimestamp: Date
  numFailedDeliveryAttempts: number
  deliveryResponseStatusCode?: number
}

export interface IPermissionRole {
  id: string
  name: string
  organizationId: string
  permissions: string[]
  createdAt: string
  updatedAt: string
  users: string[]
}

export interface ILogEvent {
  organizationId: string
  chainId: number
  blockNumber: number
  blockTimestamp: string
  logMessage: string
}

export interface IPriceQuote {
  from: string
  to: string
  value: number
  expiryTimestamp: string
}

export interface WalletShare {
  id: string
  loginId: string
  loginType: string
  share: string
  createdAt: string
}

export interface KeyValuePair {
  key: string
  value: string
}

export interface CheckoutItemData {
  name: string
  description?: string
  chargingIntervalSeconds?: number
  price: string
  imageUrl?: string
  isMetered?: boolean
  currency: string
}

export interface ICheckoutSession {
  id: string
  organizationId: string
  total: number
  currency?: string
  successUrl: string
  cancelUrl?: string
  sessionStatus: 'pending' | 'success' | 'cancelled' | 'expired'
  expiresAt?: string
  createdAt: string
  updatedAt: string
  customizations: Object
  items?: CheckoutItemData[]
  products?: IProduct[]
  gateway: {
    selfCustodial?: ISelfCustodialGateway
    managed?: IManagedGateway
  }
  metadata: Array<{ key: string, value: string }>
  payment?: {
    managed?: ManagedPaymentDetails
    managedTokenDelegatePayment?: ManagedPaymentDetails
    openBanking: openBankingDetails
  }
  refunds: Refund[]
  openBankingPaymentData: openBankingPaymentScheme
}

export interface Refund {
  status: string
  network: string
  token?: string
  amount: number
  chargeCustomerNetworkFee: boolean
  recipientAddress: string
  transactionHash?: string
  createdAt: string
}

export interface CheckoutOrder {
  id: string
  checkoutSessionId: string
  organizationId: string
  sellerPaymentAddress: string
  buyerAddress: string
  tokenAddress: string
  chainId: number
  orderHash: string
  createdAt: Date
  paidAt: Date
}

export interface CheckoutOrderSession {
  order: CheckoutOrder
  session: ICheckoutSession
  metadata: KeyValuePair[]
}

export interface PaymentLinkOrderData {
  key: string
  value: string
}

export interface IManagedPaymentMethodDetails {
  paymentId: string
  paymentAddress: string
  network: string
  token?: string
  amount: string
  networkFee: string
  expiresAt: string
}

export interface PaymentMethodDetails {
  managed?: IManagedPaymentMethodDetails
  selfCustodial?: {
    sellerAddress: string
    buyerAddress: string
    tokenAddress: string
    chainId: number
    orderHash: string
    orderInput: string
  }
}

export interface ManagedTransaction {
  network: string
  token: string
  amount: string
  transactionHash: string
  blockTimestamp: string
}

export interface PaymentLinkOrder {
  id: string
  paymentLinkId: string
  paymentMethodDetails: PaymentMethodDetails
  payment: {
    managed?: ManagedPaymentDetails
    managedTokenDelegatePayment?: ManagedPaymentDetails
  }
  orderData: PaymentLinkOrderData[]
  paidAt: string
  createdAt: string
}

export interface DonationLinkOrder {
  id: string
  donationLinkId: string
  paymentMethodDetails: PaymentMethodDetails
  payment: {
    managed?: ManagedPaymentDetails
    managedTokenDelegatePayment?: ManagedPaymentDetails
  }
  orderData: PaymentLinkOrderData[]
  paidAt: string
  createdAt: string
  refunds: Refund[]
}

export interface ConversionRate {
  from: string
  to: string
  rate: string
}

export interface Payments {
  managed?: ManagedPaymentDetails
  openBanking?: openBankingPaymentDetails
}

export interface ManagedPaymentDetails {
  id?: string
  paymentEventId: string
  amount: string
  conversionRates: ConversionRate[]
  network: string
  token?: string
  networkFee: string
  transactions: ManagedTransaction[]
}

export interface openBankingPaymentDetails {
  id?: string
  paymentEventId: string
  amount: string
  network: string
  token?: string
  status?: string
  onrampStatus: string
  openBankingPaymentData?: OpenBankingPaymentData
}

export interface openBankingDetails {
  id?: string
  paymentEventId: string
  amount: string
  network: string
  token?: string
  status?: string
  onrampStatus: string
  onrampGrossAmount?: string
  onrampNetAmount?: string
  onrampNetwork?: string
  onrampToken?: string
  onrampFee?: string
  openBankingPaymentData?: OpenBankingPaymentData
}
export interface IBANAddress {
  iban: string
  accountHolderName?: string
  bic?: string | null
}

export interface PayerFinancialAddress {
  iban?: IBANAddress
  sortCode?: SortCode
  bankCode?: BankCode
  bban?: Bban
}

export interface OrderStatusHistoryEntry {
  currentStatus: string
  reason: string
  createdAt: string
  updatedAt: string
  previousStatus?: string
}

export interface openBankingPaymentScheme {
  status: string
  paymentScheme?: string
  instantPaymentScheme?: boolean
  payerFinancialAddress?: PayerFinancialAddress
  statusHistoryList?: OrderStatusHistoryEntry[]
  shopper?: Shopper
}

export interface Shopper {
  email?: string
  phoneNumber?: string
}

export interface Metadata {
  key: string
  value: string
}

export interface ManagedBalance {
  network: string
  token?: string
  treasuryId?: string
  balance: string
}

export interface PrivateTreasury {
  id: string
  organizationId: string
  evmAddress: string
  status: 'pendingActivation' | 'active' | 'activationFailed'
  createdAt: string
  activatedAt?: string
}

export interface ConversionQuoteResponse {
  amount: string
}

export interface WithdrawalRequestReceipt {
  EVMReceipt?: {
    transactionHash: string
    networkFeeAmount?: string
  }
  BTCReceipt?: {
    transactionId: string
    networkFeeAmount?: string
  }
  SOLReceipt?: {
    signature: string
    networkFeeAmount?: string
  }
  DOTReceipt?: {
    transactionHash: string
    networkFeeAmount?: string
  }
  FiatReceipt?: {
    transactionHash?: string
    flatFee?: string
    percentFee?: string
    transferMethod?: string
    withdrawalAccountId?: string
    finalAmount?: string
  }
}

export enum WithdrawalStatus {
  Pending = 'pending',
  Complete = 'complete',
  Processing = 'processing',
  Failed = 'failed',
  PendingApproval = 'pendingApproval',
  InReview = 'inReview',
}

export interface WithdrawalRequest {
  id: string
  organizationId: string
  network: string
  token?: string
  withdrawalAmount: string
  withdrawalAddress: string
  withdrawalAccountId: string
  requestedAt: string
  withdrawalStatus: WithdrawalStatus
  completedAt?: string
  receipt?: WithdrawalRequestReceipt
  destination?: WithdrawalDestination
  treasuryId?: string
  failureReason?: string
}

export interface WithdrawalRequestWithAccountName extends WithdrawalRequest {
  withdrawalAccountName?: string
}

export interface DepositMethod {
  Crypto?: {
    network: string
    token?: string
  }
  Fiat?: {
    fiatCurrency: string
    onrampNetwork: string
    onrampToken?: string
  }
}

export interface SenderAddress {
  address: string
}

export interface DepositTransaction {
  network: string
  transactionHash: string
  token?: string
  amount: number
  blockTimestamp: string
  senderAddresses?: SenderAddress[]
}

export type DepositStatus = 'complete' | 'inReview' | 'failed'

export interface Deposit {
  id: string
  depositAccountId: string
  depositAccountName: string
  organizationId: string
  depositMethod: DepositMethod
  fiatGrossAmount?: number
  grossAmount: number
  netAmount: number
  fee: number
  conversionRates: ConversionRate[]
  depositedAt: string
  status: DepositStatus
  depositTransaction?: DepositTransaction
}

export interface ListWithdrawalsResponse {
  data: WithdrawalRequest[]
  total: number
}

// Legacy PayoutEntry type (kept for backward compatibility with old views)
export interface PayoutEntry {
  id: string
  payoutType: {
    crypto?: {
      network: string
      token?: string
      amount: string
      payoutAddress: string
      initialAmount?: string
      customerId?: string
    }
  }
  status:
    | 'AwaitingConfirmation'
    | 'Pending'
    | 'Processing'
    | 'Complete'
    | 'Expired'
    | 'Failed'
  payoutEntryReceipt?: {
    crypto?: {
      txHash: string
    }
  }
}

export interface PayoutEntryHedge {
  fundingCurrency: PayoutFundingCurrency
  fundingNetwork: string
  fundingToken?: string
  lockedRate: string
  midPrice: string
  guaranteedAmount: string
  feeBuffer: string
  providerWithdrawalFee: string
  totalCryptoToAcquire: string
  stableForTrade: string
  conversionFee: string
  stablecoinCost: string
  swapId?: string
  deliveryWithdrawalId?: string
}

// New payout entry types matching backend PayoutResponse
export interface CryptoPayoutEntryResponse {
  id: string
  status: string
  amount: string
  customerId?: string
  network: string
  token?: string
  payoutAddress: string
  txHash?: string
  externalAccountId?: string
  fundingCurrency?: PayoutFundingCurrency
  failureReason?: string
  hedge?: PayoutEntryHedge
}

export interface FiatPayoutEntryResponse {
  id: string
  status: string
  amount: string
  customerId?: string
  currency: string
  iban?: string
  sortCode?: string
  bankCode?: string
  bban?: string
  beneficiaryName?: string
  beneficiaryEmail?: string
  beneficiaryCountry?: string
  beneficiaryDob?: string
  beneficiaryCompanyName?: string
  beneficiaryAddressLine1?: string
  beneficiaryAddressLine2?: string
  beneficiaryCity?: string
  beneficiaryPostalCode?: string
  beneficiaryState?: string
  providerReference?: string
  externalAccountId?: string
  failureReason?: string
}

export interface Payout {
  id: string
  status: 'AwaitingConfirmation' | 'Pending' | 'Processing' | 'Complete' | 'Expired' | 'InReview' | 'Failed'
  accountType?: string
  totalAmountUsd: string
  estimatedFeeUsd: string
  actualFeeUsd?: string
  entryCount: number
  fundingCurrency?: string
  fundingWithdrawalIds?: string[]
  fundingConversionIds?: string[]
  processingStage?: string
  cryptoEntries: CryptoPayoutEntryResponse[]
  fiatEntries: FiatPayoutEntryResponse[]
  createdAt: string
  confirmedAt?: string
  completedAt?: string
  expiresAt: string
  // Legacy fields (kept for backward compat)
  payouts: PayoutEntry[]
  deductFees: boolean
  sendEmailReceipts: boolean
  totalPayoutUsd: string
  receipt?: any
  feeBreakdown: any
}

export interface ListPayoutsResponse {
  data: Payout[]
  total: number
}

export interface PayoutFeeEstimate {
  feePercent: number
  flatFeeUsd: number
  minimumFeeUsd?: number
  estimatedFeeUsd: number
  estimatedFundingAmountUsd?: number | null
  estimatedTotalDebitUsd?: number | null
  estimatedRecipientAmount: number
  exchangeRate?: number | null
  networkFeeUsd?: number
}

export type PayoutAmountMode = 'recipient' | 'source'

export type PayoutQuoteSide = 'to' | 'from'

export type PayoutFundingCurrency = 'USDC' | 'USDT' | 'BTC' | 'ETH' | 'SOL'

export interface FundedPayoutDestinationCapability {
  fundingCurrency: PayoutFundingCurrency
  network: string
  token?: string
}

export interface PayoutCapabilitiesResponse {
  fundedDestinations: FundedPayoutDestinationCapability[]
}

// Bank account types for fiat payout destinations (reuses Iban/SortCode/BankCode/Bban below)
export interface FiatPayoutBankAccount {
  iban?: { iban: string, accountHolderName?: string, bic?: string }
  sortCode?: { sortCode: string, accountNumber: string, accountHolderName?: string }
  bankCode?: { code: string, accountNumber: string, accountHolderName?: string }
  bban?: { bban: string, accountHolderName?: string, bic?: string }
}

// New CreatePayoutRequest matching backend API
export interface CreatePayoutRequest {
  payouts: CreatePayoutEntity[]
  accountType?: string
  isTestMode?: boolean
  deductFees?: boolean
  sendEmailReceipts?: boolean
  autoConfirm?: boolean
  // Retry-safe creation: resubmitting with the same key returns the payout the
  // first attempt created instead of creating a second one.
  idempotencyKey?: string
}

export interface CreatePayoutBeneficiary {
  customerId?: string
  accountType?: 'Individual' | 'Business'
  name?: string
  email?: string
  countryOfResidence?: string
  dateOfBirth?: string
  companyName?: string
  addressLine1?: string
  addressLine2?: string
  townOrCity?: string
  postalCode?: string
  stateOrProvince?: string
}

export interface CreateCryptoPayoutEntity extends CreatePayoutBeneficiary {
  gateway: 'crypto'
  amount: string
  amountMode?: PayoutAmountMode
  side?: PayoutQuoteSide
  payoutAddress: string
  network: string
  token?: string
  fundingCurrency?: PayoutFundingCurrency
}

export interface CreateFiatPayoutEntity extends CreatePayoutBeneficiary {
  gateway: 'fiat'
  amount: string
  amountMode?: PayoutAmountMode
  externalAccountId: string
}

export interface CreateFiatWithdrawalAccountPayoutEntity extends CreatePayoutBeneficiary {
  gateway: 'fiatWithdrawalAccount'
  amount: string
  amountMode?: PayoutAmountMode
  withdrawalAccountId: string
  fundingCurrency?: PayoutFundingCurrency
}

export type CreatePayoutEntity =
  | CreateCryptoPayoutEntity
  | CreateFiatPayoutEntity
  | CreateFiatWithdrawalAccountPayoutEntity

// Legacy create type kept for backward compat in older views/components
export interface CreatePayoutEntityLegacy {
  amount: string
  name?: string
  email: string
  countryOfResidence?: string
  dateOfBirth?: string
  companyName?: string
  addressLine1?: string
  addressLine2?: string
  townOrCity?: string
  postalCode?: string
  stateOrProvince?: string
  gateway: 'crypto' | 'fiat'
  externalAccountId: string
}

// Legacy create types (kept for backward compat)
export interface CreatePayoutEntryLegacy {
  crypto?: {
    network: string
    token?: string
    amount: string
    payoutAddress: string
    email?: string
    name?: string
    dateOfBirth?: string
    residenceCountry?: string
    accountType?: string
    companyNumber?: string
  }
  fiat?: {
    network: string
    token?: string
    amount: string
    currency: string
    accountOwnerName: string
    accountOwnerType: 'Individual' | 'Business'
    accountType: FiatAccountDetails
  }
}

export interface PaymentSummary {
  currency: string
  feeAmount: string
  grossAmount: string
  netAmount: string
  networkFeeAmount: string
  overpaidAmount: string
  radomFeeAmount: string
  underpaidAmount: string
}

export interface IConversionRate {
  from: string
  to: string
  rate: string
}

export interface ManagedEventTransaction {
  network: string
  transactionHash: string
  token?: string
  amount?: string | number
  blockTimestamp?: string
  senderAddresses?: SenderAddress[]
}

export interface OpenBankingPaymentData {
  status?: string
  bankStatementReference?: string
  onrampStatus?: string
  payerFinancialAddress?: PayerFinancialAddress
  bankId?: string
  bankName?: string
  bankMarket?: string
  paymentScheme?: string
  instantPaymentScheme?: boolean
  statusHistoryList?: OrderStatusHistoryEntry[]
  shopper?: Shopper
  paymentReference?: string
  endToEndId?: string
  remittanceInformation?: string
  senderName?: string
  senderIban?: string
  senderBic?: string
  senderSortCode?: string
  senderAccountNumber?: string
  senderBankCode?: string
  senderBban?: string
}

export interface ManagedEventPaymentData {
  amount: string
  paymentMethod: {
    network: string
    token: string
  }
  transactions?: ManagedEventTransaction[]
  openBankingPaymentData?: OpenBankingPaymentData
  conversionRates?: IConversionRate[]
  paymentSummary: PaymentSummary | null
  radomData: {
    checkoutSession?: {
      checkoutSessionId: string
    }
    paymentLink?: {
      paymentLinkId: string
      paymentLinkOrderId: string
    }
    invoice?: {
      invoiceId: string
    }
    paymentSession?: {
      paymentSessionId: string
    }
    subscription?: {
      subscriptionId: string
    }
    donationLink?: {
      donationLinkId: string
      donationLinkOrderId: string
    }
  }
}

export interface ManagedEventNewSubscription {
  id: string
  subscriptionId: string
  subscriptionType: {
    automatedEVMSubscription?: {
      buyerAddress: string
      subscriptionContractAddress: string
      recommendedAllowanceDuration?: number
    }
  }
  network: string
  token?: string
  amount: string
  currency: string
  period: ManagedSubscriptionPeriod
  periodCustomDuration?: number
  tags?: { [key: string]: string }
  createdAt: string
}

export type ManagedEventSubscriptionCancelled = ManagedEventNewSubscription & {
  cancelledAt: string
}
export type ManagedEventSubscriptionExpired = ManagedEventNewSubscription & {
  expiredAt: string
}

export interface WithdrawalDestination {
  uetr?: string
  currency?: string
  paymentRail?: string
  traceNumber?: string
  externalAccountId?: string
}

export interface ManagedEventWithdrawalData {
  withdrawalRequestId: string
  network: string
  token?: string
  withdrawalAmount: string
  isSuccess: boolean
  failureReason?: string
  receipt?: WithdrawalRequestReceipt
  destination?: WithdrawalDestination
}

export interface DepositBeneficiaryDetails {
  // wire
  imad?: string
  bank_name?: string
  payment_rail?: string
  payment_scheme?: string
  wire_message?: string
  originator_name?: string
  sender_name?: string
  originator_iban?: string
  sender_iban?: string
  sender_account_number?: string
  originator_address?: string
  bank_routing_number?: string
  sender_bank_name?: string
  bank_beneficiary_name?: string
  bank_beneficiary_address?: string
  reference?: string
  payment_reference?: string
  end_to_end_id?: string
  remittance_information?: string
  // sepa
  bic?: string
  iban?: string
  uetr?: string
  iban_last_4?: string
  recipient_name?: string
  // ach
  description?: string
  ach_memo?: string
  trace_number?: string
  estimated_arrival_date?: string
  sender_bank_routing_number?: string
}

export interface ManagedEventDepositData {
  depositId: string
  depositAccountId: string
  depositMethod: DepositMethod
  netAmount: number
  feeAmount: number
  fiatGrossAmount: number
  grossAmount: number
  depositedAt: string
  beneficiaryDetails?: DepositBeneficiaryDetails
  transactionHash?: string
  senderAddresses?: SenderAddress[]
}

export interface ManagedEventData {
  payment: ManagedEventPaymentData
  openBanking: ManagedEventPaymentData
  withdrawal: ManagedEventWithdrawalData
  refund: ManagedEventWithdrawalData
  recurringPayment: ManagedEventPaymentData
  incompletePayment: ManagedEventPaymentData
  newSubscription: ManagedEventNewSubscription
  subscriptionCancelled: ManagedEventSubscriptionCancelled
  subscriptionExpired: ManagedEventSubscriptionExpired
  subscriptionPayment: ManagedEventPaymentData
  deposit: ManagedEventDepositData
}

export type ManagedEventType =
  | 'payment'
  | 'openBanking'
  | 'withdrawal'
  | 'recurringPayment'
  | 'newSubscription'
  | 'incompletePayment'
  | 'refund'
  | 'subscriptionPayment'
  | 'subscriptionCancelled'
  | 'subscriptionExpired'
  | 'deposit'

export interface ManagedEvent {
  id: string
  organizationId: string
  eventType: ManagedEventType
  eventData: ManagedEventData
  eventDate: string
}

export interface ManagedEventWithRelatedCustomer {
  id: string
  organizationId: string
  eventType: ManagedEventType
  eventData: ManagedEventData
  eventDate: string
  relatedCustomer?: RelatedCustomer
  amountsBreakdown?: ManagedEventAmountBreakdown
}

export interface ManagedPaymentStatus {
  isComplete: boolean
  network?: string
  token?: string
  amount?: BigNumber
}

export type PaymentSessionStatus =
  | 'pending'
  | 'success'
  | 'cancelled'
  | 'expired'

export interface PaymentSession {
  id: string
  method: IPaymentMethod
  total: number
  currency: string
  sessionStatus: PaymentSessionStatus
  paymentTotal: number
  paymentAddress?: string
  transactions: ManagedTransaction[]
  expiresAt?: string
  createdAt: string
  payment?: {
    managed?: ManagedPaymentDetails
    managedTokenDelegatePayment?: ManagedPaymentDetails
  }
  refunds: Refund[]
}

export interface Payment {
  id: string
  organizationId: string
  associatedEntityType: AssociatedEntityType
  associatedEntityId: string
  currency?: string
  subtotal?: number
  total?: number
  network: string
  token?: string
  amount: number
  networkFee: number
  conversionRates: ConversionRate[]
  discountCodeId?: string
  discountAmount?: number
  feeAamount?: number
  netAmount?: number
  customerId?: string
  startedAt: string
  completedAt: string
  paymentEventId: string
}

export type ManagedSubscriptionPeriod =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'annually'
  | 'custom'

export type SubscriptionStatus = 'active' | 'cancelled' | 'expired'

type AssociatedEntityType =
  | 'paymentLinkOrder'
  | 'checkoutSession'
  | 'invoice'
  | 'paymentSession'
  | 'DonationLinkOrder'
export interface ISubscription {
  product?: any
  id: string
  organizationId: string
  associatedEntityType: AssociatedEntityType
  associatedEntityId: string
  subscriptionType: {
    automatedEVMSubscription?: {
      buyerAddress: string
      emailAddress?: string
      subscriptionContractAddress: string
      recommendedAllowanceDuration?: number
    }
    emailInvoiceSubscription?: {
      emailAddress: string
    }
  }
  status: SubscriptionStatus
  network: string
  token?: string
  amount: string
  currency: string
  period: ManagedSubscriptionPeriod
  periodCustomDuration?: number
  tags?: { [key: string]: string }
  nextBillingDateAt: string
  createdAt: string
  expiredAt?: string
  cancelledAt?: string

  payments?: Array<{
    date: string
    network: string
    token: string
    amount: string
    netAmount: string
    feeAmount: string
    networkFee: string
    transactionHashes: string[]
  }>
}

export interface User {
  id: string
  emailAddress?: string
  evmAddress?: string
  defaultOrganizationId: string
  firstName?: string
  lastName?: string
  imageUrl?: string
  otpEnabled: boolean
  otpVerified: boolean
  permissions?: string[]
}

export interface TeamInvite {
  id: string
  roles: string[]
  emailAddress?: string
  evmAddress?: string
  status: any
  createdAt: string
  inviteUrl: string
}

export interface Member {
  userId: string
  emailAddress?: string
  evmAddress?: string
  firstName?: string
  lastName?: string
  imageUrl?: string
  lastLoginAt?: string
}

export interface Invite {
  id: string
  organizationId: string
}

export interface SearchResult {
  managedEvent?: ManagedEvent
  checkoutSession?: ICheckoutSession
}

export interface RefundPayerAddress {
  line1?: string
  line2?: string
  city: string
  postalCode?: string
  country: string
}

export interface RefundRequest {
  paymentSessionId?: string
  checkoutSessionId?: string
  donationLinkOrderId?: string
  refundAmount: string
  chargeCustomerNetworkFee?: boolean
  recipientAddress: string
  payerAddress?: RefundPayerAddress
}

export interface FiatAccountDetails {
  firstName?: string
  lastName?: string
  iban?: {
    iban: string
    bic: string
    addressV2?: Address
  }
  us?: {
    accountNumber: string
    routingNumber: string
    bankName: string
    addressV2?: Address
  }
}

export interface FiatAccountType {
  accountOwnerType: string
  accountOwnerName: string
  accountType: FiatAccountDetails
}

export interface WithdrawalAccountDetails {
  Crypto?: {
    network: string
    token?: string
    address: string
  }
  Fiat?: FiatAccountType
}

export interface WithdrawalAccount {
  id: string
  organizationId: string
  accountType: 'crypto' | 'fiat'
  name: string
  status: 'pending' | 'verfied'
  accountDetails: WithdrawalAccountDetails
  createdAt: string
}

interface PaginatedResponse<Type> {
  data: Type[]
  totalCount: number
  limit: number
  offset: number
}

export interface CreateDepositAccountReq {
  Crypto?: {
    depositMethods: IPaymentMethod[]
    isExternal: boolean
    customerId?: string
    treasuryId?: string
  }
  Fiat?: {
    currency: string
    network: string
    token?: string
  }
}

export enum DepositCryptoAddressType {
  FirstParty = 'First party',
  ThirdParty = 'Third party',
}

export enum ProductsUsageType {
  Invoicing = 'Invoicing',
  IntegratedPayments = 'Integrated Payments',
  Donations = 'Donations',
  Subscriptions = 'Subscriptions',
  VirtualAccounts = 'Virtual Accounts',
  OnRamp = 'On Ramp',
  OffRamp = 'Off Ramp',
  PaymentsLinks = 'Payment Links',
  CryptoPresaleWidget = 'Crypto Presale Widget',
  DedicatedDepositAddresses = 'Dedicated Deposit Addresses',
  OpenbankingPaymentRamp = 'Openbanking Payment Ramp',
  Payouts = 'Payouts',
  MassFiatPayouts = 'Mass Fiat Payouts',
  Openbanking = 'Open Banking',
  MassCryptoPayouts = 'Mass Crypto Payouts',
}

export enum MonthlyVolumeType {
  GettingStarted = 'Just getting started (no payment volume yet)',
  UpTo100k = 'Up to $100,000/month',
  UpTo500k = '$100,001 - $500,000/month',
  UpTo2M = '$500,001 - $2,500,000/month',
  UpTo10M = '$2,500,001 - $10,000,000/month',
  UpTo50M = '$10,000,001 - $50,000,000/month',
  MoreThan50M = 'Over $50,000,000/month',
}

export enum PaymentCounterpartiesType {
  B2B = 'B2B',
  C2B = 'C2B',
  B2C = 'B2C',
}

export enum BusinessActivityPeriods {
  MoreThan3Years = 'More than 3 years',
  Between1And3Years = 'More than 1 year, less than 3 years',
  LessThan1Year = 'Less than 1 year',
}

export enum RadomDiscover {
  SocialMedia = 'Social Media (X, LinkedIn, etc.)',
  SearchEngine = 'Search Engine (Google, Bing, etc.)',
  Referral = 'Referral from a friend or colleague',
  Advertisement = 'Online Advertisement',
  BlogOrNewsArticle = 'Blog or News Article',
  ConferenceOrEvent = 'Conference or Event',
  Other = 'Other',
}

export interface FiatDepositAccountDetails {
  iban?: {
    iban: string
    bic: string
    accountHolderName?: string
    sourceCurrency?: string
    sourcePaymentRails?: string[]
    destinationCurrency?: string
    destinationPaymentRail?: string
  }
  us?: {
    accountNumber: string
    routingNumber: string
    bankBeneficiaryName?: string
    bankBeneficiaryAddress?: string
    sourceCurrency?: string
    sourcePaymentRails?: string[]
    destinationCurrency?: string
    destinationPaymentRail?: string
  }
  spei?: {
    clabe: string
    accountHolderName?: string
    sourceCurrency?: string
    sourcePaymentRails?: string[]
    destinationCurrency?: string
    destinationPaymentRail?: string
  }
  pix?: {
    brCode: string
    accountHolderName?: string
    sourceCurrency?: string
    sourcePaymentRails?: string[]
    destinationCurrency?: string
    destinationPaymentRail?: string
  }
}

export interface FiatDepositAccountType {
  bankName: string
  bankAddress: string
  accountType: FiatDepositAccountDetails
}

export interface CryptoDepositAccountDetails {
  Crypto: {
    address: string
    depositMethods: IPaymentMethod[]
  }
}

export enum DepositAccountType {
  Fiat = 'fiat',
  Crypto = 'crypto',
}

export interface DepositAccountDetails {
  Crypto?: {
    address: string
    depositMethods: IPaymentMethod[]
    isExternal?: boolean
    customerId?: string
    treasuryId?: string
  }
  Fiat?: FiatDepositAccountType
}

export interface DepositAccount {
  id: string
  organizationId: string
  name: string
  status: 'active' | 'disabled'
  accountDetails: DepositAccountDetails
  isMaster: boolean
  createdAt: string
  bastionId?: string
  customer?: RelatedCustomer
}

export interface RelatedCustomer {
  id: string
  name: string
  email: string
}

export interface Trigger {
  minimumAmount?: {
    amount: string
    currency: string
  }
  timeInterval?: {
    interval: string
  }
  withdrawalAmount?: {
    amount: string
    currency: string
  }
}

export interface Automation {
  id: string
  organizationId: string
  name: string
  automationType: 'withdrawal'
  automationDetails: {
    Withdrawal?: {
      assets: Array<{ network: string, token?: string }>
      triggers: Trigger[]
      withdrawalAccountId: string
    }
  }
  lastTriggeredAt?: string
  createdAt: string
}

export interface VisitorTokenRequest {
  email: string
  first_name: string
  last_name: string
}

export interface VisitorTokenResponse {
  token: string
}

export interface VerifyOrganizationRequest {
  organizationType: 'Individual' | 'Business' | 'Nonprofit'
  name?: string
  website?: string
  country?: string
  industry?: string
}

export interface VerifyOrganizationResponse {
  personaVerificationLink: string
}

export type BridgeCustomerStatus =
  | 'Created'
  | 'TosApproved'
  | 'Pending'
  | 'Accepted'
  | 'Rejected'

export interface BridgeCustomer {
  id: string
  tosLink: string
  status: BridgeCustomerStatus
  organizationId: string
}

// ============================================
// External Accounts
// ============================================

export type ExternalAccountType = 'us' | 'iban' | 'clabe' | 'pix' | 'gb' | 'unknown' | 'crypto'
export type ExternalAccountOwnerType = 'individual' | 'business'

export interface ExternalAccountAddress {
  street_line_1: string
  street_line_2?: string
  city: string
  state?: string
  postal_code?: string
  country: string
}

export interface UsExternalBankAccount {
  account_number: string
  routing_number: string
  checking_or_savings?: string
}

export interface IbanExternalBankAccount {
  account_number: string
  bic?: string
  country: string
}

export interface ClabeExternalBankAccount {
  account_number: string
}

export interface PixKeyExternalBankAccount {
  pix_key: string
  document_number?: string
}

export interface BrCodeExternalBankAccount {
  br_code: string
  document_number?: string
}

export interface GbExternalBankAccount {
  account_number: string
  sort_code: string
}

export interface SwiftExternalBankAccount {
  account_number: string
  bic: string
  country?: string
  category?: string
  purpose_of_funds?: string[]
  short_business_description?: string
  bank_address?: ExternalAccountAddress
}

export interface CryptoExternalAccount {
  address: string
  network: string
}

export interface CreateExternalAccountRequest {
  customer_id: string
  currency: string
  account_type: ExternalAccountType
  bank_name?: string
  account_owner_name: string
  account_owner_type?: ExternalAccountOwnerType
  first_name?: string
  last_name?: string
  business_name?: string
  address?: ExternalAccountAddress
  account?: UsExternalBankAccount
  iban?: IbanExternalBankAccount
  clabe?: ClabeExternalBankAccount
  pix_key?: PixKeyExternalBankAccount
  br_code?: BrCodeExternalBankAccount
  gb_account?: GbExternalBankAccount
  swift?: SwiftExternalBankAccount
  crypto?: CryptoExternalAccount
}

export interface ExternalAccount {
  id: string
  customerId: string
  organizationId: string
  currency: string
  accountType: ExternalAccountType
  bankName?: string
  accountOwnerName: string
  accountOwnerType?: ExternalAccountOwnerType
  firstName?: string
  lastName?: string
  businessName?: string
  active: boolean
  createdAt: string
  updatedAt: string
  account?: { last4: string, routingNumber: string, checkingOrSavings?: string }
  iban?: { last4: string, bic?: string, country?: string }
  clabe?: { last4: string }
  pixKey?: { accountPreview?: string, documentNumberLast4?: string }
  brCode?: { accountPreview?: string, documentNumberLast4?: string }
  gbAccount?: { last4: string, sortCode: string }
  swift?: {
    last4: string
    bic: string
    country?: string
    category?: string
    purposeOfFunds?: string[]
    shortBusinessDescription?: string
  }
  crypto?: { address: string, network: string }
}

export interface Onramp {
  id: string
  status: string
  amount: string
  paymentRail: string
  fromCurrency: string
  toNetwork: string
  toToken: string
  toAddress: string
  paymentDetails: {
    currency: string
    amount: string
    depositMessage: string
    iban: string
    bic: string
    accountHolderName: string
    bankName: string
    bankAddress: string
  }
  receipt?: {
    finalAmount: string
    txHash: string
  }
  createdAt: string
  completedAt?: string
}

export interface Iban {
  iban: string
  accountHolderName?: string
  bic?: string
}

export interface SortCode {
  sortCode: string
  accountNumber: string
  accountHolderName?: string
}

export interface BankCode {
  code: string
  accountNumber: string
  accountHolderName?: string
}

export interface Bban {
  bban: string
  accountHolderName?: string
  bic?: string
}

export enum RefundStatus {
  Pending = 'pending',
  Success = 'success',
  Cancelled = 'cancelled',
  Failed = 'failed',
  InReview = 'inReview',
}

export interface RefundEntity {
  id: string
  organizationId: string
  withdrawalId?: string
  associatedEntityType: AssociatedEntityType
  associatedEntityId: string
  status: RefundStatus
  network: string
  token?: string
  amount: string
  chargeCustomerNetworkFee: boolean
  recipientAddress?: string
  createdAt: string
  recipientAccount?: PayerFinancialAddress
  ivyOrderId?: string
  failureReason?: string
  initiatedByUserId?: string
  initiatedByAdminUserId?: string
}

export interface RefundEntityWithRelatedCustomer extends RefundEntity {
  relatedCustomer?: RelatedCustomer
}

export enum TransactionKind {
  Payout = 'Payout',
  Withdrawal = 'Withdrawal',
  Deposit = 'Deposit',
  Payment = 'Payment',
  Refund = 'Refund',
  Swap = 'Swap',
}

export enum SwapStatus {
  Pending = 'Pending',
  Initiated = 'Initiated',
  Failed = 'Failed',
  Finalized = 'Finalized',
}

export enum SwapProvider {
  OKX = 'OKX',
  Bastion = 'Bastion',
}

export interface Swap {
  id: string
  status: SwapStatus
  orgId: string
  swapSettingsId?: string
  externalId?: string
  fromNetwork: string
  fromToken?: string
  fromAmount: string
  fromTransactionId?: string
  fromTxid?: string
  toNetwork: string
  toToken?: string
  toTransactionId?: string
  toTxid?: string
  toAmount?: string
  swapFee?: string
  failReason?: string
  provider: SwapProvider
  fundsAvailable?: boolean
  createdAt: string
  updatedAt: string
}

export enum TransactionStatus {
  Successful = 'Successful',
  Pending = 'Pending',
  Processing = 'Processing',
  InReview = 'InReview',
  Incomplete = 'Incomplete',
  Failed = 'Failed',
}

export interface Transaction {
  id: string
  kind: TransactionKind
  createdAt: string
  payload:
    | Payout
    | ManagedEventWithRelatedCustomer
    | WithdrawalRequestWithAccountName
    | Deposit
    | RefundEntityWithRelatedCustomer
    | Swap
}

export enum WithdrawalTransferType {
  Withdrawal = 'withdrawal',
  Refund = 'refund',
  PayoutFunding = 'payoutFunding',
  OfframpFunding = 'offrampFunding',
  Swap = 'swap',
  OnChainSwapFunding = 'onChainSwapFunding',
}

export enum GeneralDepositMethod {
  Fiat = 'Fiat',
  Crypto = 'Crypto',
}

export interface ISwapRequest {
  from: {
    network: string
    token?: string
  }
  into: {
    network: string
    token?: string
  }
  amount: number
}

export interface ISwapQuoteRateComponents {
  spreadPercent: number | string
  slippageBps: number
  slippageBpsLeg2?: number
  hedgingBps: number
}

export interface ISwapQuoteResponse {
  amount: number | string
  fee: number | string
  rate: number | string
  marketRate: number | string
  radomFee: number | string
  radomFeePercent: number | string
  networkFee: number | string
  rateComponents: ISwapQuoteRateComponents
}

export interface ISwapSupportedAssetsResponse {
  assets: Array<{
    network: string
    token?: string | null
  }>
}

export interface IAutoSwapOnPaymentSettings {
  enabled: boolean
  targetNetwork: string | null
  targetToken: string | null
}

export interface BridgeVirtualAccountCapabilities {
  canOpenAch: boolean
  canOpenSepa: boolean
  canOpenSpei: boolean
  canOpenPix: boolean
}

export interface PooledEurOnrampInstructions {
  available: boolean
  cashDepositEnabled?: boolean
  cashWithdrawalEnabled?: boolean
  reference?: string
  iban?: string
  bic?: string
  beneficiary?: string
  bankName?: string
}

export interface DepositAccountMetrics {
  totalCount: number
  latestName: string | null
  latestCreatedAt: string | null
  volume30d: number
}

export interface ManagedBalanceLedger {
  id: string
  organizationId: string
  network: string
  token?: string
  treasuryId?: string
  balanceBefore: string
  balanceAfter: string
  associatedId?: string
  transactionId: string
  transactionType: string
  createdAt: string
}

export interface ManagedEventAmountBreakdown {
  networkFee: string
  network: string
  amount: string
  currency?: string
  onrampFee?: string
  onrampGrossAmount?: string
  onrampNetAmount?: string
  onrampNetwork?: string
  onrampToken?: string
}
export interface VerificationRequirement {
  fieldId: string
  comment: string
  currentValue: string
}

export const DEFAULT_ORGANIZATION_ID = '00000000-0000-0000-0000-000000000000'

export interface ISupportTicket {
  id: string
  status: 'open' | 'awaiting_reply' | 'resolved' | string
  priority: string
  source: string
  subject: string
  category?: string
  unread?: boolean
  firstResponseAt?: string
  resolvedAt?: string
  createdAt: string
  updatedAt: string
}

export interface ISupportMessage {
  id: string
  direction: 'inbound' | 'outbound'
  author: 'you' | 'assistant' | 'agent'
  body: string
  createdAt: string
}

export interface ISupportTicketList {
  tickets: ISupportTicket[]
  total: number
}

export interface ISupportTicketDetail {
  ticket: ISupportTicket
  messages: ISupportMessage[]
}

export interface ISupportChannelCode {
  code: string
  expiresAt: string
}

class Radom {
  isLoggedIn = false
  requires2fa = false
  isLoggingIn = false
  sellerProfile: IOrganization | null = null
  token = ''

  constructor() {
    axios.interceptors.response.use((res) => res, async (res) => {
      return await Promise.reject(res)
    })
    makeAutoObservable(this)
    this.token = localStorage.getItem(RADOM_TOKEN_LOCAL_STORAGE_KEY) ?? ''
    if (this.token) {
      this.isLoggedIn = true
    }
  }

  login = async (): Promise<void> => {
    this.isLoggingIn = true

    try {
      const address = await UserState.connectedAccount

      const authRequest: IAuthRequestResponse = await this.post(
        '/auth/request',
        { address }
      )

      const signedMessage = await UserState.signer?.signMessage(
        authRequest.nonce
      )

      const verifyResponse: IAuthTokenResponse = await this.post(
        '/auth/verify',
        {
          address,
          signature: signedMessage
        }
      )

      this.token = verifyResponse.jwtToken
      try {
        localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, this.token)
        localStorage.setItem(
          RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY,
          new Date(verifyResponse.expiresAt).toString()
        )
      } catch (err) {
        localStorage.clear()
        window.location = '/' as any
      }

      this.isLoggedIn = !verifyResponse.requires2fa
      this.requires2fa = verifyResponse.requires2fa
      if (!verifyResponse.requires2fa) {
        successToast('Successfully signed in!')
      }

      // Ensure token is set before proceeding
      if (this.token) {
        try {
          const visitorTokenResponse = await this.generateVisitorToken()
          localStorage.setItem('visitorToken', visitorTokenResponse.token)
          await identifyVisitor()
        } catch {}
      }
    } catch (err) {
      console.error('Failed to login', err)
      this.isLoggingIn = false
      throw err
    }

    this.isLoggingIn = false
  }

  loginWithSSO = async (loginType: string, token: string, tokenType?: string): Promise<void> => {
    this.isLoggingIn = true

    try {
      const verifyResponse: IAuthTokenResponse = await this.post('/auth/sso', {
        loginType,
        token,
        tokenType
      })

      this.token = verifyResponse.jwtToken
      try {
        localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, this.token)
        localStorage.setItem(
          RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY,
          new Date(verifyResponse.expiresAt).toString()
        )
      } catch (err) {
        localStorage.clear()
        window.location = '/' as any
      }

      if (verifyResponse.isNewUser === true) {
        trackSignUp(loginType === 'google' ? 'google' : 'email')
      }
      this.isLoggedIn = !verifyResponse.requires2fa
      this.requires2fa = verifyResponse.requires2fa
      if (!verifyResponse.requires2fa) {
        successToast('Successfully signed in!')
      }

      if (this.token) {
        try {
          const visitorTokenResponse = await this.generateVisitorToken()
          localStorage.setItem('visitorToken', visitorTokenResponse.token)
          await identifyVisitor()
        } catch {}
      } else {
        throw new Error('Token not set')
      }
    } catch (err) {
      console.error('Failed to login', err)
      errorToast(err.reason || err.message)
    }

    this.isLoggingIn = false
  }

  signOut = (): void => {
    localStorage.clear()
    window.location = '/' as any
  }

  getProducts = async (
    isArchived: boolean = false,
    limit = 1000,
    offset = 0,
    orderBy = 'updated_at',
    orderDirection = 'desc'
  ): Promise<{
    data: IProduct[]
    total: number
  }> => {
    return await this.get('/products', {
      isArchived,
      limit,
      offset,
      orderBy,
      orderDirection
    })
  }

  verifyOrganizationWithParent = async (
    req: { parentOrganizationId: string }
  ): Promise<void> => {
    return await this.post('/organization/verify_with_parent', req)
  }

  listDiscountCodes = async (): Promise<IDiscountCode[]> => {
    return await this.get('/discount_codes')
  }

  getSellerProfile = async (): Promise<IOrganization> => {
    return await this.get('/seller_profile').then((s: IOrganization) => {
      this.sellerProfile = s
      return s
    })
  }

  getOrganizationById = async (id: string): Promise<IOrganization> => {
    return await this.get(`/sellers/${id}`)
  }

  getOrganization = async (): Promise<IOrganization> => {
    return await this.get('/organization')
  }

  getVirtualAccountsCapabilities = async (): Promise<
  BridgeVirtualAccountCapabilities
  > => {
    return await this.get('/deposit/virtual_accounts_capabilities')
  }

  getPooledEurOnrampInstructions = async (): Promise<PooledEurOnrampInstructions> => {
    return await this.get('/deposit/pooled_eur_onramp')
  }

  getVerification = async (): Promise<IVerification | undefined> => {
    const verification = await this.get<IVerification>('/verification')

    return verification
  }

  getOrgFees = async (): Promise<IOrgFees> => {
    const response = await this.get<{ fees: IOrgFees }>('/organization/fees')
    return response.fees
  }

  updateVerification = async (
    payload: UpdateVerificationPayload = null
  ): Promise<void> => {
    await this.put('/verification', payload)
  }

  uploadVerificationImage = async (data: any): Promise<string> => {
    return await this.postForm('/verification/data', data)
  }

  getSwapRates = async (data: ISwapRequest): Promise<ISwapQuoteResponse> => {
    return await this.post('/swap/quote', data)
  }

  initiateSwap = async (data: ISwapRequest): Promise<void> => {
    return await this.post('/swap', data)
  }

  getSwap = async (id: string): Promise<Swap> => {
    return await this.get(`/swap/${id}`)
  }

  getSwapSupportedAssets = async (): Promise<ISwapSupportedAssetsResponse> => {
    return await this.get('/swap/supported_assets')
  }

  getSwapSupportedTargets = async (from: {
    network: string
    token?: string
  }): Promise<ISwapSupportedAssetsResponse> => {
    return await this.post('/swap/supported_targets', { from })
  }

  getAutoSwapOnPayment = async (): Promise<IAutoSwapOnPaymentSettings> => {
    return await this.get('/swap/auto_on_payment')
  }

  updateAutoSwapOnPayment = async (
    payload: IAutoSwapOnPaymentSettings
  ): Promise<IAutoSwapOnPaymentSettings> => {
    return await this.put('/swap/auto_on_payment', payload)
  }

  updateSellerProfile = async (
    profile: Partial<IOrganization>
  ): Promise<void> => {
    await this.put('/organization', profile)
  }

  getProduct = async (productId: string): Promise<IProduct> => {
    return await this.get(`/product/${productId}`)
  }

  updateProduct = async (
    productId: string,
    productInfo: IProductUpdate
  ): Promise<IProduct> => {
    return await this.put(`/product/${productId}`, productInfo)
  }

  createProduct = async (productInfo: IProductCreate): Promise<IProduct> => {
    return await this.post('/product/create', productInfo)
  }

  createPaymentLink = async (
    req: CreateUpdatePaymentLinkReq
  ): Promise<IPaymentLink> => {
    return await this.post('/payment_link/create', req)
  }

  createDonationLink = async (
    donationLinkReq: IDonationLink
  ): Promise<IDonationLink> => {
    return await this.post('/donation_link/create', donationLinkReq)
  }

  updateDonationLink = async (
    donationLinkReq: IDonationLink
  ): Promise<void> => {
    return await this.put(
      `/donation_link/${donationLinkReq.id}`,
      donationLinkReq
    )
  }

  createDiscountCode = async (discountCodeReq: {
    code: string
    products: string[]
    model: { modelType: string, amountOff: number }
    startsAt?: Date
    expiresAt?: Date
  }): Promise<void> => {
    return await this.post('/discount_code/create', discountCodeReq)
  }

  deleteDiscountCode = async (discountCodeId: string): Promise<void> => {
    return await this.delete(`/discount_code/${discountCodeId}`)
  }

  getPaymentLink = async (paymentLinkId: string): Promise<IPaymentLink> => {
    return await this.get(`/payment_link/${paymentLinkId}`)
  }

  getPayment = async (id: string): Promise<Payment> => {
    return await this.get(`/payment/${id}`)
  }

  listPaymentsByPaymentLinkId = async (
    paymentLinkId: string,
    limit: number,
    offset = 0
  ): Promise<{
    data: Payment[]
    total: number
  }> => {
    return await this.get(
      `/payment_link/${paymentLinkId}/payments?limit=${limit}&offset=${offset}`
    )
  }

  getPaymentByTransactionHash = async (txHash: string): Promise<Payment> => {
    return await this.get(`/payment/by_transaction/${txHash}`)
  }

  listPaymentLinks = async (
    types: any[] = [],
    limit: number = 0,
    offset = 0,
    orderBy = 'updated_at',
    orderDirection = 'DESC',
    isArchivedValues?: boolean[]
  ): Promise<{
    data: IPaymentLink[]
    total: number
  }> => {
    const typesStr = types.map((f, i) => `paymentLinkTypes[${i}]=${f}`).join(
      '&'
    )
    const archivedStr = (isArchivedValues ?? [])
      .map((v, i) => `&isArchived[${i}]=${v}`)
      .join('')
    return await this.get(
      `/payment_links?${typesStr}&limit=${limit}&offset=${offset}&orderBy=${orderBy}&orderDirection=${orderDirection}${archivedStr}`
    )
  }

  getDonationLinks = async (): Promise<IDonationLink[]> => {
    return await this.get('/donation_links')
  }

  getDonationLink = async (id: string): Promise<IDonationLink> => {
    return await this.get(`/donation_link/${id}`)
  }

  updatePaymentLink = async (
    id: string,
    req: CreateUpdatePaymentLinkReq
  ): Promise<IPaymentLink> => {
    return await this.put(`/payment_link/${id}`, req)
  }

  deletePaymentLink = async (id: string): Promise<void> => {
    return await this.delete(`/payment_link/${id}`)
  }

  archivePaymentLink = async (id: string): Promise<void> => {
    return await this.put(`/payment_link/${id}/archive`, {})
  }

  deleteDonationLink = async (id: string): Promise<void> => {
    return await this.delete(`/donation_link/${id}`)
  }

  archiveDonationLink = async (id: string): Promise<void> => {
    return await this.put(`/donation_link/${id}/archive`, {})
  }

  deleteProduct = async (id: string): Promise<void> => {
    return await this.delete(`/product/${id}`)
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Customers
  // -------------------------------------------------------------------------------------------------------------------
  createCustomer = async (customerForm: ICreateCustomer): Promise<Customer> => {
    const data = {
      ...customerForm,
      email: customerForm.email.toLowerCase()
    }
    return await this.post('/customer', data)
  }

  updateCustomer = async (
    id: string,
    req: { tags: string[] }
  ): Promise<Customer> => {
    return await this.put(`/customer/${id}`, req)
  }

  getCustomer = async (id: string, networks?: string[]): Promise<Customer> => {
    let networksStr
    if (networks) {
      networksStr = networks.map((f, i) => `networks[${i}]=${f}`).join('&')
    }
    return await this.get(
      `/customer/${id}${networksStr ? `?${networksStr}` : ''}`
    )
  }

  getCustomerByEmail = async (email: string): Promise<Customer> => {
    return await this.get(`/customer?email=${encodeURIComponent(email)}`)
  }

  getCustomerSubscriptions = async (id: string): Promise<ISubscription[]> => {
    return await this.get(`/customer/${id}/subscriptions`)
  }

  getCustomerDeposits = async (id: string): Promise<Deposit[]> => {
    return await this.get(`/customer/${id}/deposits`)
  }

  deleteCustomer = async (id: string): Promise<void> => {
    return await this.delete(`/customer/${id}`)
  }

  listCustomers = async (
    offset: number,
    limit: number,
    networks: string[],
    sortBy?: string
  ): Promise<{
    data: Customer[]
    total: number
  }> => {
    const networksStr = networks.map((f, i) => `networks[${i}]=${f}`).join('&')
    return await this.get(
      `/customers?sortBy=${sortBy}&offset=${offset}&limit=${limit}&${networksStr}`
    )
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Invoices
  // -------------------------------------------------------------------------------------------------------------------
  createInvoice = async (data: MultiCreateInvoice): Promise<Invoice> => {
    const invoiceData: ICreateInvoice = {
      ...data,
      customerIds: data.customers.map((c) => c.id),
      products: data.products.map((p) => {
        return {
          ...p,
          quantity: Number(p.quantity),
          product: {
            ...p.product,
            price: Number(p.product.price)
          }
        }
      }),
      lineItems: data.lineItems.map((l) => {
        return {
          ...l,
          quantity: Number(l.quantity),
          price: Number(l.price)
        }
      })
    }
    return await this.post('/invoice', invoiceData)
  }

  getInvoice = async (id: string): Promise<Invoice> => {
    return await this.get(`/invoice/${id}`)
  }

  getSellerInvoices = async (): Promise<ListInvoice[]> => {
    return await this.get('/invoices')
  }

  voidInvoice = async (invoiceId: string): Promise<void> => {
    return await this.delete(`/invoice/${invoiceId}`)
  }

  updateLastBlockAck = async (data: LatestAck[]): Promise<void> => {
    return await this.put('/notifications', data)
  }

  getLastBlockAck = async (): Promise<LatestAck[]> => {
    return await this.get('/notifications')
  }

  generateAPIToken = async (
    name: string,
    permissions?: string[]
  ): Promise<ICreateApiTokenResponse> => {
    return await this.post('/api_token', { name, permissions })
  }

  generateOneTimePassword = async (): Promise<OtpAuthResponse> => {
    return await this.post('/auth/otp/generate')
  }

  verifyGeneratedOneTimePassword = async (
    token: string
  ): Promise<OtpAuthResponse> => {
    return await this.post('/auth/otp/verify', { token })
  }

  verifyOneTimePassword = async (
    token: string
  ): Promise<IAuthTokenResponse> => {
    return await this.post('/auth/otp/login', { token })
  }

  disableOneTimePassword = async (): Promise<void> => {
    return await this.post('/auth/otp/disable')
  }

  deleteAPIToken = async (tokenRef: number | string): Promise<void> => {
    return await this.delete(`/api_token/${tokenRef}`)
  }

  listAPITokens = async (): Promise<IApiTokenListItem[]> => {
    return await this.get('/api_tokens')
  }

  getWebhooks = async (addr: string): Promise<IWebhook[]> => {
    return await this.get('/webhooks')
  }

  getWebhook = async (id: string): Promise<IWebhook> => {
    return await this.get(`/webhook/${id}`)
  }

  createWebhook = async (webhookInfo: { url: string }): Promise<IWebhook> => {
    return await this.post('/webhook/create', webhookInfo)
  }

  updateWebhook = async (id: string, webhookInfo: {
    url: string
    verificationKey?: string
    isPaused: boolean
    isDeleted: boolean
  }): Promise<void> => {
    await this.put(`/webhook/${id}`, webhookInfo)
  }

  deleteWebhook = async (id: string): Promise<void> => {
    return await this.delete(`/webhook/${id}`)
  }

  getWebhookMessages = async (
    id: string,
    limit: number,
    offset: number,
    status: WebhookMessagesStatus[]
  ): Promise<{
    data: IWebhookMessage[]
    total: number
  }> => {
    const params = { limit, offset, status }
    return await this.get(`/webhook/${id}/messages`, params)
  }

  archiveProduct = async (
    productId: string,
    isArchived: boolean
  ): Promise<IProduct> => {
    return await this.put(
      `/product/${productId}/is_archived/${isArchived}`,
      isArchived
    )
  }

  resendWebhookMessageFailures = async (id: string): Promise<IWebhook> => {
    return await this.post(`/webhook/${id}/resend_message_failures`, {})
  }

  resendWebhookMessage = async (
    id: string,
    messageId: string
  ): Promise<void> => {
    return await this.post(`/webhook/${id}/message/${messageId}/resend`, {})
  }

  sendTestWebhook = async (
    id: string
  ): Promise<{ success: boolean, statusCode?: number, error?: string }> => {
    return await this.post(`/webhook/${id}/test`, {})
  }

  listPermissionRoles = async (): Promise<IPermissionRole[]> => {
    return await this.get('/role')
  }

  deletePermissionRole = async (id: string): Promise<void> => {
    return await this.delete(`/role/${id}`)
  }

  createPermissionRole = async (
    permissionsRequest: { name: string, permissions: string[] }
  ): Promise<void> => {
    return await this.post('/role', permissionsRequest)
  }

  updatePermissionRole = async (
    id: string,
    permissionsRequest: { name: string, permissions: string[] }
  ): Promise<void> => {
    return await this.put(`/role/${id}`, permissionsRequest)
  }

  inviteMember = async (
    req: { emailAddress?: string, evmAddress?: string, roles: string[] }
  ): Promise<TeamInvite> => {
    return await this.post('/invite', req)
  }

  listInvites = async (): Promise<TeamInvite[]> => {
    return await this.get('/invite')
  }

  listMembers = async (): Promise<Member[]> => {
    return await this.get('/member')
  }

  deleteInvite = async (inviteId: string): Promise<void> => {
    return await this.delete(`/invite/${inviteId}`)
  }

  editMember = async (userId: string, roles: string[]): Promise<void> => {
    return await this.post(`/member/${userId}`, { roles })
  }

  deleteMember = async (userId: string): Promise<void> => {
    return await this.delete(`/member/${userId}`)
  }

  getPriceQuotes = async (
    from: string[],
    to: string[]
  ): Promise<IPriceQuote[]> => {
    return await this.get('/price_quotes', { from, to })
  }

  getCheckoutSession = async (id: string): Promise<ICheckoutSession> => {
    return await this.get(
      `/checkout_session/${id}`
    )
  }

  listCheckoutSessions = async (offset: number, limit: number, status?: ICheckoutSession['sessionStatus']): Promise<{
    data: ICheckoutSession[]
    total: number
  }> => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    if (status) params.set('status', status)
    return await this.get(`/checkout_sessions?${params}`)
  }

  createCheckoutSession = async (req: ICreateCheckoutSession): Promise<any> => {
    return await this.post(
      '/checkout_session',
      req
    )
  }

  cancelCheckoutSession = async (checkoutSessionId: string): Promise<any> => {
    return await this.post(`/checkout_session/${checkoutSessionId}/cancel`)
  }

  getWalletShare = async (
    loginType: string,
    token: string
  ): Promise<WalletShare> => {
    return await this.get(
      `/get_wallet_share?loginType=${loginType}&token=${token}`
    )
  }

  storeWalletShare = async (
    loginType: string,
    token: string,
    walletShare: string
  ): Promise<void> => {
    return await this.post('/store_wallet_share', {
      loginType,
      token,
      walletShare
    })
  }

  getPaymentLinkOrder = async (
    paymentLinkOrderId: string
  ): Promise<PaymentLinkOrder> => {
    return await this.get(`/payment_link_order/${paymentLinkOrderId}`)
  }

  getDonationLinkOrder = async (
    donationLinkOrderId: string
  ): Promise<DonationLinkOrder> => {
    return await this.get(`/donation_link_order/${donationLinkOrderId}`)
  }

  getManagedBalance = async (): Promise<ManagedBalance[]> => {
    return await this.get('/managed_balance')
  }

  getManagedBalanceForTreasury = async (treasuryId: string): Promise<ManagedBalance[]> => {
    return await this.get(`/managed_balance?treasuryId=${treasuryId}`)
  }

  getPrivateTreasury = async (): Promise<PrivateTreasury | null> => {
    return await this.get('/private_treasury')
  }

  createPrivateTreasury = async (): Promise<PrivateTreasury> => {
    return await this.post('/private_treasury', {})
  }

  activatePrivateTreasury = async (): Promise<PrivateTreasury> => {
    return await this.post('/private_treasury/activate', {})
  }

  submitWithdrawalRequest = async (
    network: string,
    amount: string,
    withdrawalAccountId: string,
    senderPaysFees: boolean,
    token?: string,
    treasuryId?: string
  ): Promise<void> => {
    return await this.post('/withdrawal', {
      network,
      token,
      amount,
      withdrawalAccountId,
      senderPaysFees,
      treasuryId
    })
  }

  listWithdrawals = async (
    offset: number,
    limit: number,
    networks: string[]
  ): Promise<ListWithdrawalsResponse> => {
    const networksStr = networks.map((f, i) => `networks[${i}]=${f}`).join('&')
    return await this.get(
      `/withdrawal?limit=${limit}&offset=${offset}&${networksStr}`
    )
  }

  getWithdrawal = async (id: string): Promise<WithdrawalRequest> => {
    return await this.get(`/withdrawal/${id}`)
  }

  confirmPayout = async (id: string): Promise<void> => {
    return await this.post(`/payout/${id}/confirm`)
  }

  quotePayout = async (id: string): Promise<void> => {
    return await this.post(`/payout/${id}/quote`)
  }

  getPayoutFee = async (params: {
    currency: string
    amount: string
    amountMode?: PayoutAmountMode
    accountType?: string
    network?: string
    token?: string
    fundingCurrency?: PayoutFundingCurrency
  }): Promise<PayoutFeeEstimate> => {
    const searchParams = new URLSearchParams()
    searchParams.set('currency', params.currency)
    searchParams.set('amount', params.amount)
    if (params.amountMode) searchParams.set('amountMode', params.amountMode)
    if (params.accountType) searchParams.set('accountType', params.accountType)
    if (params.network) searchParams.set('network', params.network)
    if (params.token) searchParams.set('token', params.token)
    if (params.fundingCurrency) searchParams.set('fundingCurrency', params.fundingCurrency)
    return await this.get(`/payout/fee?${searchParams.toString()}`)
  }

  getPayoutCapabilities = async (): Promise<PayoutCapabilitiesResponse> => {
    return await this.get('/payout/capabilities')
  }

  createPayout = async (req: CreatePayoutRequest): Promise<Payout> => {
    return await this.post('/payout', req)
  }

  getPayout = async (id: string): Promise<Payout> => {
    return await this.get(`/payout/${id}`)
  }

  listPayouts = async (
    offset: number,
    limit: number,
    isTestMode: boolean,
    statuses: string[],
    isMassPayout?: boolean
  ): Promise<ListPayoutsResponse> => {
    const statusesStr = statuses
      .map((f, i) => `statuses[${i}]=${f}`)
      .join('&')
    const isMassPayoutStr = isMassPayout === undefined ? '' : `&isMassPayout=${isMassPayout}`
    return await this.get(
      `/payouts?limit=${limit}&offset=${offset}&isTestMode=${isTestMode}${isMassPayoutStr}&${statusesStr}`
    )
  }

  getPayoutReceipt = async (payoutId: string): Promise<{ url: string, message: string }> => {
    return await this.get(`/receipt/${payoutId}`)
  }

  listTransactions = async (
    offset: number,
    limit: number,
    kinds: TransactionKind[],
    statuses: TransactionStatus[],
    networks: string[],
    from: string,
    to: string,
    isTestMode: boolean,
    search?: string
  ): Promise<{
    data: Transaction[]
    total: number
  }> => {
    const statusesStr = statuses.map((f, i) => `statuses[${i}]=${f}`).join('&')
    const networksStr = networks.map((f, i) => `networks[${i}]=${f}`).join('&')
    const kindsStr = kinds.map((f, i) => `kinds[${i}]=${f}`).join('&')
    const transferTypesStr = [
      WithdrawalTransferType.OfframpFunding,
      WithdrawalTransferType.Withdrawal
    ].map((f, i) => `withdrawalTypes[${i}]=${f}`).join('&')

    const queryParts = [
      `limit=${limit}`,
      `offset=${offset}`,
      `isTestMode=${isTestMode}`,
      statusesStr,
      networksStr,
      kindsStr,
      transferTypesStr,
      `from=${from}`,
      `to=${to}`,
      search ? `search=${encodeURIComponent(search)}` : ''
    ].filter(Boolean)

    return await this.get(
      `/transactions?${queryParts.join('&')}`
    )
  }

  listPaginatedEvents = async (
    offset: number,
    limit: number,
    eventTypes: string[],
    networks: string[]
  ): Promise<{
    data: ManagedEvent[]
    total: number
  }> => {
    const eventTypesStr = eventTypes.map((f, i) => `eventTypes[${i}]=${f}`)
      .join('&')
    const networksStr = networks.map((f, i) => `networks[${i}]=${f}`).join('&')
    return await this.get(
      `/managed/payments?limit=${limit}&offset=${offset}&${eventTypesStr}&${networksStr}`
    )
  }

  listOnramps = async (
    offset: number,
    limit: number,
    statuses: string[]
  ): Promise<{
    data: Onramp[]
    totalCount: number
  }> => {
    const statusesStr = statuses.map((f, i) => `statuses[${i}]=${f}`).join('&')
    return await this.get(
      `/onramps?limit=${limit}&offset=${offset}&${statusesStr}`
    )
  }

  getOnramp = async (id: string): Promise<Onramp> => {
    return await this.get(`/onramp/${id}`)
  }

  listManagedEvents = async (
    networks: string[],
    from: string,
    to: string
  ): Promise<{
    data: ManagedEvent[]
    total: number
  }> => {
    const eventTypes = [
      'payment',
      'openBanking',
      'withdrawal',
      'recurringPayment',
      'newSubscription',
      'incompletePayment',
      'subscriptionCancelled',
      'subscriptionExpired',
      'refund',
      'subscriptionPayment',
      'deposit'
    ].map((e, i) => `eventTypes[${i}]=${e}`).join('&')
    const networksStr = networks.map((f, i) => `networks[${i}]=${f}`).join('&')
    return await this.get(
      `/managed/events?${eventTypes}&from=${from}&to=${to}&${networksStr}`
    )
  }

  getCheckoutFunnelSummary = async (
    from: string,
    to: string
  ): Promise<CheckoutFunnelSummaryRow[]> => {
    return await this.get('/checkout/funnel/summary', { from, to })
  }

  getEvent = async (id: string): Promise<ManagedEvent> => {
    return await this.get(`/managed/event/${id}`)
  }

  getDepositEvent = async (id: string): Promise<ManagedEvent> => {
    return await this.get(`/managed/event/deposit/${id}`)
  }

  getWithdrawalEvent = async (id: string): Promise<ManagedEvent> => {
    return await this.get(`/managed/event/withdrawal/${id}`)
  }

  getManagedPaymentStatus = async (
    paymentId: string
  ): Promise<ManagedPaymentStatus> => {
    return await this.get(`/managed_payment_status/${paymentId}`)
  }

  getPaymentSession = async (
    paymentSessionId: string
  ): Promise<PaymentSession> => {
    return await this.get(`/payment_session/${paymentSessionId}`)
  }

  listSubscriptions = async (
    createdBefore: string,
    cancelledAfter: string,
    expiredAfter: string,
    networks: string[],
    statuses: SubscriptionStatus[],
    offset: number,
    limit: number
  ): Promise<{
    data: ISubscription[]
    total: number
  }> => {
    const networksStr = networks.map((f, i) => `networks[${i}]=${f}`).join('&')
    const statusesStr = statuses.map((f, i) => `statuses[${i}]=${f}`).join('&')
    return await this.get(
      `/v2/subscriptions?createdBefore=${createdBefore}&cancelledAfter=${cancelledAfter}&expiredAfter=${expiredAfter}&${networksStr}&offset=${offset}&limit=${limit}&${statusesStr}`
    )
  }

  getSubscription = async (id: string): Promise<ISubscription> => {
    return await this.get(`/subscription/${id}`)
  }

  cancelSubscription = async (id: string): Promise<void> => {
    return await this.post(`/subscription/${id}/cancel`, {})
  }

  getInvite = async (inviteId: string): Promise<Invite> => {
    return await this.get(`/invite/${inviteId}`)
  }

  acceptInvite = async (inviteId: string): Promise<Invite> => {
    return await this.post(`/invite/${inviteId}/accept`)
  }

  listOrganizations = async (): Promise<string[]> => {
    return await this.get('/organizations')
  }

  updateUser = async (
    req: { defaultOrganizationId: string }
  ): Promise<void> => {
    const verifyResponse: IAuthTokenResponse = await this.put('/user', req)
    this.token = verifyResponse.jwtToken
    if (this.token) {
      try {
        const visitorTokenResponse = await this.generateVisitorToken()
        localStorage.setItem('visitorToken', visitorTokenResponse.token)
        await identifyVisitor()
      } catch {}
    }
    try {
      localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, this.token)
      localStorage.setItem(
        RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY,
        new Date(verifyResponse.expiresAt).toString()
      )
    } catch (err) {
      localStorage.clear()
    }
    window.location = '/' as any
  }

  emailSignIn = async (
    email: string,
    password: string,
    recaptchaToken: string
  ): Promise<IAuthTokenResponse> => {
    return await this.post('/auth/email/login', {
      email,
      password,
      recaptchaToken
    })
  }

  startEmailSignUp = async (
    email: string,
    recaptchaToken: string
  ): Promise<void> => {
    return await this.post('/auth/email/start_signup', {
      email,
      recaptchaToken
    })
  }

  completeEmailSignUp = async (
    email: string,
    password: string,
    code: string
  ): Promise<IAuthTokenResponse> => {
    return await this.post('/auth/email/complete_signup', {
      email,
      password,
      code
    })
  }

  startPasswordReset = async (
    email: string,
    recaptchaToken: string
  ): Promise<void> => {
    return await this.post('/auth/email/start_password_reset', {
      email,
      recaptchaToken
    })
  }

  completePasswordReset = async (
    email: string,
    newPassword: string,
    code: string
  ): Promise<IAuthTokenResponse> => {
    return await this.post('/auth/email/complete_password_reset', {
      email,
      newPassword,
      code
    })
  }

  search = async (searchstring: string): Promise<SearchResult[]> => {
    return await this.get(`/search?value=${encodeURIComponent(searchstring)}`)
  }

  exportPayments = async (
    from: string,
    to: string,
    columns: string[],
    paymentType: string
  ): Promise<any> => {
    const columnsJoined = columns.map((f, i) => `columns[${i}]=${f}`).join('&')
    return await this.get(
      `/export/payments?from=${from}&to=${to}&${columnsJoined}&paymentType=${paymentType}`
    )
  }

  exportWithdrawals = async (
    from: string,
    to: string,
    columns: string[],
    statuses: string[]
  ): Promise<any> => {
    const columnsJoined = columns.map((f, i) => `columns[${i}]=${f}`).join('&')
    const statusesJoined = statuses.map((f, i) => `statuses[${i}]=${f}`).join(
      '&'
    )
    return await this.get(
      `/export/withdrawals?from=${from}&to=${to}&${columnsJoined}&${statusesJoined}`
    )
  }

  exportRefunds = async (
    from: string,
    to: string,
    columns: string[],
    statuses: string[]
  ): Promise<any> => {
    const columnsJoined = columns.map((f, i) => `columns[${i}]=${f}`).join('&')
    const statusesJoined = statuses.map((f, i) => `statuses[${i}]=${f}`).join(
      '&'
    )
    return await this.get(
      `/export/refunds?from=${from}&to=${to}&${columnsJoined}&${statusesJoined}`
    )
  }

  exportDeposits = async (
    isTestMode: boolean,
    from: string,
    to: string,
    columns: string[]
  ): Promise<any> => {
    const columnsJoined = columns.map((f, i) => `columns[${i}]=${f}`).join('&')
    return await this.get(
      `/export/deposits?from=${from}&to=${to}&${columnsJoined}&isTestMode=${isTestMode}`
    )
  }

  exportPayouts = async (
    isTestMode: boolean,
    from: string,
    to: string,
    columns: string[]
  ): Promise<any> => {
    const columnsJoined = columns.map((f, i) => `columns[${i}]=${f}`).join('&')
    return await this.get(
      `/export/payouts?from=${from}&to=${to}&${columnsJoined}&isTestMode=${isTestMode}`
    )
  }

  exportPayoutEntries = async (id: string, columns: string[]): Promise<any> => {
    const columnsJoined = columns.map((f, i) => `columns[${i}]=${f}`).join('&')
    return await this.get(`/export/payout_entries?id=${id}&${columnsJoined}`)
  }

  exportBalance = async (
    from: string,
    to: string,
    period: any
  ): Promise<any> => {
    return await this.get(
      `/export/balance?from=${from}&to=${to}&period=${period}`
    )
  }

  refund = async (req: RefundRequest): Promise<void> => {
    return await this.post('/refund', req)
  }

  getRefund = async (id: string): Promise<RefundEntity> => {
    return await this.get(`/refund/${id}`)
  }

  claimTestTokens = async (
    req: { network: string, token: string, address: string }
  ): Promise<{ txId: string }> => {
    return await this.post('/claim_test_tokens', req)
  }

  createOrganization = async (): Promise<{ organizationId }> => {
    return await this.post('/organization')
  }

  completePayment = async (params: any): Promise<void> => {
    return await this.post('/complete_payment', params)
  }

  updateNotificationSettings = async (newSettings: any): Promise<void> => {
    return await this.post('/notification_settings', newSettings)
  }

  getNotificationSettings = async (): Promise<
  { [setting: string]: boolean }
  > => {
    return await this.get('/notification_settings')
  }

  listWithdrawalAccounts = async (): Promise<WithdrawalAccount[]> => {
    return await this.get('/withdrawal/account')
  }

  getWithdrawalAccount = async (id: string): Promise<WithdrawalAccount> => {
    return await this.get(`/withdrawal/account/${id}`)
  }

  deleteWithdrawalAccount = async (
    id: string
  ): Promise<WithdrawalAccount[]> => {
    return await this.delete(`/withdrawal/account/${id}`)
  }

  deleteDepositAccount = async (id: string): Promise<void> => {
    return await this.delete(`/deposit/account/${id}`)
  }

  createWithdrawalAccount = async (req: {
    name: string
    accountDetails: WithdrawalAccountDetails
  }): Promise<WithdrawalAccount[]> => {
    return await this.post('/withdrawal/account', req)
  }

  createDepositAccount = async (req: {
    name: string
    accountDetails: CreateDepositAccountReq
  }): Promise<void> => {
    return await this.post('/deposit/account', req)
  }

  getMasterDepositAccount = async (
    network: string,
    token?: string
  ): Promise<DepositAccount> => {
    return await this.get(
      `/deposit/account/master?network=${encodeURIComponent(network)}${
        token ? `&token=${encodeURIComponent(token)}` : ''
      }`
    )
  }

  // -------------------------------------------------------------------------------------------------------------------
  // External Accounts
  // -------------------------------------------------------------------------------------------------------------------
  createExternalAccount = async (req: CreateExternalAccountRequest): Promise<ExternalAccount> => {
    return await this.post('/external_account', req)
  }

  getExternalAccount = async (id: string): Promise<ExternalAccount> => {
    return await this.get(`/external_account/${id}`)
  }

  listExternalAccounts = async (customerId: string): Promise<ExternalAccount[]> => {
    return await this.get(`/customer/${customerId}/external_accounts`)
  }

  deleteExternalAccount = async (id: string): Promise<void> => {
    return await this.delete(`/external_account/${id}`)
  }

  listDeposits = async (
    isTestMode: boolean,
    depositMethods: GeneralDepositMethod[],
    offset: number,
    limit: number
  ): Promise<PaginatedResponse<Deposit>> => {
    const depositMethodsJoined = depositMethods.map((f, i) =>
      `depositMethods[${i}]=${f}`
    ).join('&')
    return await this.get(
      `/deposit?isTestMode=${isTestMode}&offset=${offset}&limit=${limit}&${depositMethodsJoined}`
    )
  }

  listManagedBalanceLedger = async (
    associatedIds: string[]
  ): Promise<ManagedBalanceLedger[]> => {
    const associatedIdsJoined = associatedIds.map((f, i) =>
      `associatedIds[${i}]=${f}`
    ).join('&')
    return await this.get(`/managed_balance_ledger?${associatedIdsJoined}`)
  }

  listDepositsByAccountId = async (id: string): Promise<Deposit[]> => {
    return await this.get(`/deposits/${id}`)
  }

  listWithdrawalsByAccountId = async (id: string): Promise<WithdrawalRequest[]> => {
    return await this.get(`/withdrawals/${id}`)
  }

  getDeposit = async (id: string): Promise<Deposit> => {
    return await this.get(
      `/deposit/${id}`
    )
  }

  listDepositAccounts = async (
    isTestMode: boolean,
    offset: number,
    limit: number,
    isExternal?: boolean,
    types?: DepositAccountType[]
  ): Promise<PaginatedResponse<DepositAccount>> => {
    const typesJoined = types?.map((f, i) => `accountTypes[${i}]=${f}`).join(
      '&'
    )

    return await this.get(
      `/deposit/account?isTestMode=${isTestMode}&offset=${offset}&limit=${limit}${
        typesJoined ? `&${typesJoined}` : ''
      }${isExternal !== undefined ? `&isExternal=${isExternal}` : ''}`
    )
  }

  getDepositAccountMetrics = async (isTestMode: boolean): Promise<DepositAccountMetrics> => {
    return await this.get(`/deposit/account/metrics?isTestMode=${isTestMode}`)
  }

  listAutomations = async (): Promise<Automation[]> => {
    return await this.get('/automations')
  }

  deleteAutomation = async (id: string): Promise<void> => {
    return await this.delete(`/automation/${id}`)
  }

  createAutomation = async (req: any): Promise<Automation> => {
    return await this.post('/automation', req)
  }

  getUser = async (): Promise<User> => {
    return await axios.get(`${API_BASE_URL}/user`, {
      headers: { Authorization: this.token }
    }).then((res) => res.data).catch((err) => {
      if (err.response?.status === 401) {
        this.signOut()
      }
    })
  }

  generateVisitorToken = async (): Promise<VisitorTokenResponse> => {
    return await this.post<VisitorTokenResponse>('/visitor_token')
  }

  uploadImage = async (imageData: any): Promise<void> => {
    return await this.postForm('/image', imageData)
  }

  generateReceipt = async (
    id: string
  ): Promise<{ url: string, message: string }> => {
    return await this.get(`/receipt/${id}`)
  }

  private readonly postForm = async <T>(
    path: string,
    body: any = {}
  ): Promise<T> => {
    const headers: any = {
      Authorization: this.token
    }

    if (this.isLoggedIn) {
      await UserState.getUser()
      headers.RadomOrganization = UserState.selectedOrganizationId
    }

    return await axios.postForm(`${API_BASE_URL}${path}`, body, {
      headers: {
        ...headers,
        'content-type': 'multipart/form-data'
      }
    }).then((res) => res.data)
  }

  listSupportTickets = async (params?: { limit?: number, offset?: number }): Promise<ISupportTicketList> => {
    return await this.get('/support/tickets', params)
  }

  getSupportTicket = async (id: string): Promise<ISupportTicketDetail> => {
    return await this.get(`/support/tickets/${id}`)
  }

  createSupportTicket = async (subject: string, body: string, category: string): Promise<ISupportTicket> => {
    return await this.post('/support/tickets', { subject, body, category })
  }

  createSupportMessage = async (id: string, body: string): Promise<ISupportMessage> => {
    return await this.post(`/support/tickets/${id}/messages`, { body })
  }

  closeSupportTicket = async (id: string): Promise<ISupportTicket> => {
    return await this.post(`/support/tickets/${id}/close`)
  }

  createSupportChannelCode = async (): Promise<ISupportChannelCode> => {
    return await this.post('/support/channel_codes')
  }

  supportUnreadCount = 0

  refreshSupportUnreadCount = async (): Promise<void> => {
    if (!this.isLoggedIn) return
    try {
      const res = await this.get<{ count: number }>('/support/tickets/unread_count')
      this.supportUnreadCount = res.count
    } catch {
    }
  }

  private readonly post = async <T>(
    path: string,
    body: any = {}
  ): Promise<T> => {
    const headers: any = {
      Authorization: this.token
    }

    if (this.isLoggedIn) {
      await UserState.getUser()
      headers.RadomOrganization = UserState.selectedOrganizationId
    }

    return await axios.post(`${API_BASE_URL}${path}`, body, {
      headers
    }).then((res) => res.data)
  }

  private readonly put = async <T>(
    path: string,
    body: any = {}
  ): Promise<T> => {
    const headers: any = {
      Authorization: this.token,
      'content-type': 'application/json'
    }

    if (this.isLoggedIn) {
      await UserState.getUser()
      headers.RadomOrganization = UserState.selectedOrganizationId
    }

    return await axios.put(`${API_BASE_URL}${path}`, body, {
      headers
    }).then((res) => res.data)
  }

  private readonly delete = async <T>(
    path: string,
    body: any = {}
  ): Promise<T> => {
    const headers: any = {
      Authorization: this.token
    }

    if (this.isLoggedIn) {
      const user = await UserState.getUser()
      headers.RadomOrganization = user?.defaultOrganizationId
    }

    return await axios.delete(`${API_BASE_URL}${path}`, {
      headers
    }).then((res) => res.data)
  }

  private readonly get = async <T>(path: string, params?: any): Promise<T> => {
    const headers: any = {
      Authorization: this.token
    }

    if (this.isLoggedIn) {
      const user = await UserState.getUser()
      headers.RadomOrganization = user?.defaultOrganizationId
    }

    return await axios.get(`${API_BASE_URL}${path}`, {
      headers,
      params
    }).then((res) => res.data)
  }
}

export default new Radom()
